import { nullableOptional } from '@shigo-ui/api-client';
import { z } from 'zod';

export enum NetworkPerformanceHalf {
  First = '1H',
  Second = '2H',
  Total = 'TOTAL',
}

export enum NetworkPerformanceStatus {
  Scale = 'SCALE',
  Optimize = 'OPTIMIZE',
  Fix = 'FIX',
}

const networkPerformanceNullableNumberSchema = nullableOptional(z.number()).transform(
  (value) => value ?? null,
);

export const networkPerformanceRowSchema = z.looseObject({
  year: z.number().int(),
  month: z.string(),
  half: z.enum(NetworkPerformanceHalf),
  networkName: z.string(),
  networkId: z.union([z.string(), z.number()]).transform(String),
  revenue: z.number(),
  profit: z.number(),
  requests: z.number(),
  impressions: z.number(),
  fillRate: networkPerformanceNullableNumberSchema,
  ecpm: networkPerformanceNullableNumberSchema,
  bidRate: networkPerformanceNullableNumberSchema,
  bidFill: networkPerformanceNullableNumberSchema,
  winRate: networkPerformanceNullableNumberSchema,
  revPerRequest: networkPerformanceNullableNumberSchema,
  fillTimesEcpm: networkPerformanceNullableNumberSchema,
  startMomentumPct: networkPerformanceNullableNumberSchema,
  momGrowthPct: networkPerformanceNullableNumberSchema,
  totalScore: networkPerformanceNullableNumberSchema,
  status: nullableOptional(z.enum(NetworkPerformanceStatus)).transform((value) => value ?? null),
});

export const networkPerformanceResponseSchema = z.looseObject({
  rows: z.array(networkPerformanceRowSchema).optional().default([]),
});

export const networkPerformanceNetworkOptionSchema = z.looseObject({
  id: z.union([z.string(), z.number()]).transform(String),
  name: z.string(),
});

export const networkPerformanceNetworkOptionsSchema = z.array(
  networkPerformanceNetworkOptionSchema,
);

export interface NetworkPerformanceRequest {
  dateFrom: string;
  dateTo: string;
  networkIds?: number[] | undefined;
}

export function buildNetworkPerformanceRequest(
  dateFrom: string,
  dateTo: string,
  selectedNetworkIds: readonly string[],
): NetworkPerformanceRequest {
  const networkIds = [
    ...new Set(selectedNetworkIds.map(Number).filter((networkId) => Number.isFinite(networkId))),
  ];

  return {
    dateFrom,
    dateTo,
    ...(networkIds.length ? { networkIds } : {}),
  };
}

export type NetworkPerformanceRow = z.infer<typeof networkPerformanceRowSchema>;
export type NetworkPerformanceResponse = z.infer<typeof networkPerformanceResponseSchema>;
export type NetworkPerformanceNetworkOption = z.infer<typeof networkPerformanceNetworkOptionSchema>;

export const networkPerformancePageSizes = [10, 25, 50, 100] as const;
